<template>
  <div v-if="loading" class="d-flex justify-center">
    <Loader/>
  </div>
  <div v-else>
    <v-toolbar elevation="0">
      <router-link to="/family-members" class="text-decoration-none">
        <v-btn icon>
          <v-icon size="40">mdi-arrow-left</v-icon>
        </v-btn>
      </router-link>

      <v-toolbar-title class="ml-3"> {{ $t('BroadcastList.BroadcastNotifications') }}</v-toolbar-title>

      <v-spacer></v-spacer>

      <div class="w-25 mr-4">
        <v-select
          v-model="filters.recipients"
          :label="$t('ResidentsView.Recipients')"
          multiple
          :placeholder="$t('ResidentsView.Recipients')"
          :items="emails"
          item-text="email"
          item-value="email"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index === 0">
              <span>{{ item.email }}</span>
            </v-chip>
            <span v-if="index === 1" class="grey--text text-caption">
              (+{{ filters.recipients.length - 1 }} {{ $t('BroadcastList.Others') }})
            </span>
          </template>
        </v-select>
      </div>

      <div class="w-25">
        <v-select
          :items="[
          {text: $t('GeneralTranslations.All'), value: 'All' },
          {text: $t('BroadcastList.Push'), value:'Push'},
          {text: $t('GeneralTranslations.Email'), value:'Email'}]"
          item-text="text"
          item-value="value"
          v-model="filters.type"
          :label="$t('GeneralTranslations.Type')"
          :placeholder="$t('GeneralTranslations.Type')"
        ></v-select>
      </div>
    </v-toolbar>

    <v-data-table :items="filteredBroadcasts" :headers="headers">
      <template v-slot:header> </template>
      <template v-slot:item.sentDatetime="{ item }">
        <span v-if="item.sentDatetime">
          {{ item.sentDateTimeDisplay }}
        </span>
        <span v-else>
          -
        </span>
      </template>
      <template v-slot:item.subject="{ item }">
        <span class="h6 font-weight-bold">{{ item.subject }}</span>
      </template>
      <template v-slot:item.type="{ item }">
        <span
          v-if="item.newBroadcastbyEmail && item.newBroadcastbyPush"
          class="text-uppercase primary--text font-weight-bold"
        >

          {{$t('BroadcastList.Email/Push') }}
        </span>
        <span
          class="text-uppercase primary--text font-weight-bold"
          v-else-if="item.newBroadcastbyEmail"
        >
          {{$t('GeneralTranslations.Email')}}
        </span>
        <span
          class="text-uppercase primary--text font-weight-bold"
          v-else-if="item.newBroadcastbyPush"
        >
          {{$t('BroadcastList.Push') }}
        </span>
      </template>

      <template v-slot:item.recipients="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              icon
              dark
              v-bind="attrs"
              v-on="on"
              :disabled="
                !item.recipients || (item.recipients && !item.recipients.length)
              "
            >
              <v-icon class="black--text">
                mdi-email
              </v-icon>
            </v-btn>
          </template>
          <p
            class="m-0"
            v-for="recipient in item.recipients"
            :key="recipient.email"
          >
            <v-icon class="white--text">
              mdi-email
            </v-icon>
            {{ recipient.email }}
          </p>
        </v-tooltip>
      </template>

      <template v-slot:item.message="{ item }" @item-expanded="expand(item)">
        <div class="break-word">
          <Editor
            v-if="item.id === editing"
            :editorProp.sync="item.template.html"
          />
          <span v-else v-html="formatItemMessage(item)"></span>
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          class="ml-3"
          @click="expand(item)"
          :disabled="editing === item.id"
        >
          <v-icon v-if="item.id === expanded">
            mdi-chevron-up
          </v-icon>
          <v-icon v-else>
            mdi-chevron-down
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import firebase from "firebase";
  import Editor from "@/components/editor/Editor.vue";
  import moment from "moment";
  import Loader from "@/components/loader/Loader.vue";

  export default {
    components: {
      Editor,
      Loader
    },
    data() {
      return {
        broadcasts: [],
        templates: [],
        headers: [
          {
            text: this.$i18n.messages[this.$i18n.locale]['BroadcastList.DateCreated'],
            value: "sentDatetime",
            width: 250
          },
          {
            text: this.$i18n.messages[this.$i18n.locale]['BroadcastList.Subject'],
            value: "subject"
          },
          {
            text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Type'],
            value: "type"
          },
          {
            text: this.$i18n.messages[this.$i18n.locale]['BroadcastList.Recipients'],
            value: "recipients"
          },
          {
            text: this.$i18n.messages[this.$i18n.locale]['BroadcastList.Message'],
            value: "message",
            width: 500,
            height: 400
          },
          {
            value: "actions",
            width: 100
          }
        ],
        loading: false,
        editing: null,
        expanded: null,
        filters: {
          type: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.All'],
          recipients: []
        }
      };
    },
    async mounted() {
      this.loading = true;
      await this.$store.dispatch("getFamilyMembers");
      await this.fetchBroadcasts();
      this.loading = false;
    },
    computed: {
      filteredBroadcasts() {
        return this.broadcasts
          .filter(broadcast => {
            if (this.filters.type === "Email") {
              return broadcast.newBroadcastbyEmail;
            } else if (this.filters.type === "Push") {
              return broadcast.newBroadcastbyPush;
            }

            return broadcast;
          })
          .filter(broadcast => {
            if (!this.filters.recipients.length) return broadcast;
            return broadcast.recipients?.some(recipient =>
              this.filters.recipients.includes(recipient.email)
            );
          })
          .map(broadcast => {
            return {
              subject: broadcast?.template?.subject ?? "",
              ...broadcast,
            }
          })
      },
      emails() {
        return this.$store.state.residentModule.familyMembers;
      }
    },
    methods: {
      async fetchBroadcasts() {
        await firebase
          .firestore()
          .collection("emailTemplates")
          .where(
            "facilityId",
            "==",
            this.$store.state.authModule.currentFacility
          )
          .get()
          .then(qs => {
            this.templates = qs.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
          });

        await firebase
          .firestore()
          .collection("broadcasts")
          .where(
            "facilityId",
            "==",
            this.$store.state.authModule.currentFacility
          )
          .get()
          .then(qs => {
            this.broadcasts = qs.docs.map(doc => ({
              id: doc.id,
              template: this.templates.find(
                template => template.id === doc.data().newBroadcastTemplate
              ),
              sentDateTimeDisplay: moment(doc.data().sentDatetime * 1000).format("DD MMM, hh:mm A"),
              ...doc.data()
            }));
          });
      },
      formatItemMessage(item) {
        if(item?.template?.html?.length) {
          if (item.id === this.expanded) {
            return item.template.html;
          }

          if (item.template.html.length <= 40) {
            return item.template.html;
          }

          return item.template.html.substring(0, 40) + "...";
        }

        return "";
      },
      expand(item) {
        if (this.expanded === item.id) {
          this.expanded = null;
          return;
        }

        this.expanded = item.id;
      },
      edit(item) {
        this.editing = item.id;
      },
      save(item) {
        this.editing = null;
      },
      getRecipients(item) {
        if (!item.recipients) return;

        if (item.id === this.expanded) {
          return item.recipients;
        } else {
          return item.recipients.splice(0, 5);
        }
      }
    }
  };
</script>

<style lang="scss" src="./styles.scss"></style>
